<template>
  <div>
    <div class="row evollu-form" v-if="showFilters">

      <div class="col-12 col-sm-3">
        <label class="form-label">
          {{ $t('evocall.requests.created_at') }}
        </label>
        <RangeDatePicker
          id="selected-time-date-picker"
          v-model="filters.dateRange"
          :placeholder="$t('evocall.requests.date_range')"
          :clearable="false"
        />
      </div>

      <div class="col-12 col-md-3" v-if="isSupervisor">
        <label class="form-label">
          {{ $t('evocall.requests.accepted_by') }}
        </label>
        <multiselect
          id="acceptees-filter"
          v-model="filters.acceptees"
          :options="accepteesOptions"
          track-by="id"
          :customLabel="formatedUserLabel"
          :placeholder="accepteesOptions.length ? $t('clinical.waiting_room_statistics.all_collaborators') : $t('clinical.waiting_room_statistics.no_collaborators')"
          :allow-empty="true"
          :multiple="true"
          :disabled="!accepteesOptions.length"
          :close-on-select="false"
        >

          <template slot="clear">
            <span class="multiselect__clear" v-if="filters.acceptees.length > 0" @mousedown.prevent.stop="filters.acceptees = []"></span>
          </template>
        </multiselect>
      </div>

      <div class="col-12 col-md-3" v-if="isSupervisor">
        <label class="form-label">
          {{ $t('evocall.requests.creator') }}
        </label>
        <multiselect
          id="creators-filter"
          v-model="filters.creators"
          :options="creatorsOptions"
          track-by="id"
          :customLabel="formatedUserLabel"
          :placeholder="creatorsOptions.length ? $t('clinical.waiting_room_statistics.all_collaborators') : $t('clinical.waiting_room_statistics.no_collaborators')"
          :allow-empty="true"
          :multiple="true"
          :disabled="!creatorsOptions.length"
          :close-on-select="false"
        >

          <template slot="clear">
            <span class="multiselect__clear" v-if="filters.creators.length > 0" @mousedown.prevent.stop="filters.creators = []"></span>
          </template>
        </multiselect>
      </div>

      <div class="col-12 col-md-3" v-if="isSupervisor">
        <label class="form-label">
          {{ $t('clinical.appointments.type') }}
        </label>
        <multiselect
          id="appt-type-filter"
          v-model="filters.appointmentTypes"
          :options="appointmentTypesOptions"
          track-by="id"
          label="type"
          placeholder="---"
          :allow-empty="true"
          :multiple="true"
          :close-on-select="false"
        >

          <template slot="clear">
            <span class="multiselect__clear" v-if="filters.appointmentTypes.length > 0" @mousedown.prevent.stop="filters.appointmentTypes = []"></span>
          </template>
        </multiselect>
      </div>


    </div>

    <v-server-table
      :columns="displayColumns"
      :options="displayTableOptions"
      @row-click="onRowClicked"
      @filter="onFilterUpdate"
      id="virtualWaitingRoomRequestHistoryTable"
      ref="virtualWaitingRoomRequestHistoryTable"
      name="virtualWaitingRoomRequestHistoryTable"
    >
    <span v-if="row.created_at" slot="created_at" slot-scope="{row}">
      {{ row.created_at | localTime('YYYY-MM-DD HH:mm') }}
    </span>

      <span slot="accepted_by_nickname" slot-scope="{row}">
      {{ row.accepted_by_nickname || "N/A" }}
    </span>

      <span slot="waiting_time" slot-scope="{row}">
      {{ getRelativeTimeString(moment(row.started_at).diff(moment(row.created_at), "seconds")) }}
    </span>

      <span slot="estimated_duration" slot-scope="{row}">
      <span v-if="row.estimated_duration != null">{{ row.estimated_duration }} {{ $t('time.minutes') }}</span>
      <span v-else>--</span>
    </span>

      <span slot="appointment_duration" slot-scope="{row}">
      <span v-if="getTeleconsultationDurationSeconds(row) !== null">
        {{ getRelativeTimeString(getTeleconsultationDurationSeconds(row)) }}
      </span>
      <span v-else>--</span>
    </span>

      <span slot="total_duration" slot-scope="{row}">
      <span v-if="getTotalDurationSeconds(row) !== null">
        {{ getRelativeTimeString(getTotalDurationSeconds(row)) }}
      </span>
      <span v-else> ({{ row.estimated_duration }} {{ $t('time.minutes') }}) </span>
    </span>

      <span v-if="isSupervisor" slot="conclusion_duration" slot-scope="{row}">
      <span v-if="getConclusionDurationSeconds(row) !== null">
        {{ getRelativeTimeString(getConclusionDurationSeconds(row)) }}
      </span>
      <span v-else>--</span>
    </span>

      <span slot="dispenser_nickname" slot-scope="{row}">
      {{ row.dispenser_nickname || "N/A" }}
    </span>

    <span slot="appointmentType" slot-scope="{row}">
      {{ row.appointmentType || "N/A" }}
    </span>

    <span slot="obs" class="no-break observations__row" slot-scope="{row}">
      <div v-if="row.obs">
        <va-popover
          :message="row.obs"
          placement="left"
        >
          <i class="i-has-comment-gray"></i>
        </va-popover>
      </div>
    </span>

    </v-server-table>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { uniqBy } from 'lodash'
import TableQueryMixin from '@/mixins/TableQueryMixin'
import Multiselect from 'vue-multiselect'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import { ActivityService } from '@/api/AuthService'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import { TABLE_EVOCALL_HISTORY, TABLE_VIRTUAL_WAITING_ROOM_HISTORY } from '@/local-storage/LocalStorageKeys';
import RelativeTimeMixin from '@/mixins/RelativeTimeMixin'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import IndexTablesMixin from "@/mixins/IndexTablesMixin";

export default {
  props: {
    filters: {
      type: Object,
      required: true
    },
    service: {
      type: Object,
      required: true
    },
    component: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    },
    showFilters: {
      type: Boolean,
      required: false,
      default: false
    },
    querySearch: {
      type: String,
      required: false,
      default: null
    },
    customQueries: {
      type: Object,
      required: false,
      default: null
    }
  },
  components: {
    Multiselect,
    RangeDatePicker
  },
  mixins: [
    TableQueryMixin,
    RelativeTimeMixin,
    MultiselectLabelFormatter
  ],
  data () {
    return {
      consultationOptions: [
        {
          id:1,
          name: 'impedance'
        },
        {
          id:2,
          name: 'vocal_audiometry'
        },
        {
          id:3,
          name: 'tonal_audiometry'
        }
      ],
      baseColumns: [
        'created_at',
        'accepted_by_nickname',
        'customer_number',
        'facility_name',
        'waiting_time',
        'estimated_duration',
        'appointment_duration',
        'total_duration',
      ],
      tailColumns: [
        'dispenser_nickname',
        'appointmentType'
      ],
      queryUrl: `${this.$store.state.virtual_waiting_room_server}/v1/requests/history`,
      tableOptions: {
        columnsDropdown: false,
        saveState: true,
        filterable: true,
        sortable: ['created_at'],
        customFilters: ['acceptees', 'creators', 'start', 'end', 'serviceId', 'appointmentTypes'],
        headings: {
          'customer_number': this.$t('virtual_waiting_room.titles.customer_nr'),
          'accepted_by_nickname': this.$t('evocall.requests.accepted_by'),
          'entity.name': this.$t('evocall.requests.entity'),
          'facility.name': this.$t('evocall.requests.facility'),
          'waiting_time': this.$t('evocall.history.waiting_time'),
          'estimated_duration': this.$t('evocall.history.estimated_duration'),
          'activity.name': this.$t('evocall.requests.activity'),
          'source.name': this.$t('evocall.requests.source'),
          'type.name': this.$t('evocall.requests.type'),
          'created_at': this.$t('evocall.requests.created_at'),
          'creator.name': this.$t('evocall.requests.creator'),
          'dispenser_nickname': this.$t('evocall.requests.creator'),
          'appointment_duration': this.$t('evocall.history.teleconsultation_duration'),
          'total_duration': this.$t('evocall.history.total_duration'),
          'updated_at': this.$t('evocall.requests.updated_at'),
          'appointmentType': this.$t('clinical.appointments.type')
        },
        columnsClasses: {
          results: 'results',
          actions: 'actions',
        },
        orderBy: {
          column: 'created_at',
          ascending: 0
        }
      },
      hasLoadedLocalStorage: false,
      entityActivityUsersForPeriod: []
    }
  },
  created: function () {
    this.loadLocalStorage()
      .then(() => this.loadEntityActivityUsersForPeriod(this.filters.dateRange.start, this.filters.dateRange.end))
      .then(() => this.loadAccepteesAndCreators())
      .then(() => this.checkActiveFilters())
      .finally(() => {
        this.hasLoadedLocalStorage = true
        this.reloadTable()
      })
  },
  methods: {
    loadAccepteesAndCreators: function () {
      return new Promise((resolve) => {
        if (this.hasReadRequestsHistoryPermissions) {
          const state = LocalStorageHelper.get(TABLE_EVOCALL_HISTORY)
          if (state !== null && state.customQueries) {
            if (state.customQueries.hasOwnProperty('acceptees') && typeof state.customQueries.acceptees === 'string') {
              try {
                const accepteeIds = state.customQueries.acceptees.split(',').map(id => parseInt(id))
                this.filters.acceptees = this.accepteesOptions.filter(acceptee => accepteeIds.includes(acceptee.id))
              } catch (errors) {
                console.log(errors)
              }
            }

            if (state.customQueries.hasOwnProperty('creators') && typeof state.customQueries.creators === 'string') {
              try {
                const creatorIds = state.customQueries.creators.split(',').map(id => parseInt(id))
                this.filters.creators = this.creatorsOptions.filter(creator => creatorIds.includes(creator.id))
              } catch (errors) {
                console.log(errors)
              }
            }
          }
        }

        resolve()
      })
    },
    loadEntityActivityUsersForPeriod: function (start, end) {
      return new Promise((resolve, reject) => {
        if (start && end) {
          if (this.hasReadRequestsHistoryPermissions) {
            ActivityService.getActivityUsersByEntity({
              accessType: this.accessTypes.READ,
              infoType: this.infoTypes.HISTORY_REQUESTS,
              serviceId: this.service.id,
              start: this.moment(start).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
              end: this.moment(end).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
              activities: `${this.activities.AUDIOLOGY},${this.activities.DISPENSER_FIELD},${this.activities.DISPENSER_PREMISES}`
            })
              .then(entityActivityRecords => {
                this.entityActivityUsersForPeriod = entityActivityRecords
                resolve()
              }).catch(errors => {
              console.log(errors)
              reject(errors)
            })
          } else {
            resolve()
          }
        } else {
          reject(new Error('Load Entity Activity Users For Period - Invalid args'))
        }
      })
    },
    loadLocalStorage: function () {
      return new Promise((resolve) => {
        const state = LocalStorageHelper.get(TABLE_VIRTUAL_WAITING_ROOM_HISTORY)
        let filters = JSON.parse(JSON.stringify(this.filters))

        if (state !== null && state.customQueries) {
          if (state.customQueries.hasOwnProperty('users') && state.customQueries.users !== null && state.customQueries.users !== '') {
            let userIds = state.customQueries.users.split(',').map(id => parseInt(id))
            filters.users = this.users.filter(d => userIds.includes(d.id))
          }

          if (state.customQueries.start && state.customQueries.end) {
            filters.dateRange.start = state.customQueries.start
            filters.dateRange.end = state.customQueries.end
          }
          if(state.customQueries.appointmentTypes && state.customQueries.appointmentTypes !== '') {
            const filteredIds = state.customQueries.appointmentTypes.split(',').map(id => parseInt(id))

            this.filters.appointmentTypes = this.appointmentTypesOptions.filter(elem => elem.bdIds.some(id => filteredIds.includes(id)))
          }


          this.filters = filters
        }
        resolve()
      })
    },
    checkActiveFilters: function () {
      return new Promise((resolve) => {
        this.$emit('filters-updated', this.activeFilters)
        if (this.activeFilters > 0) {
          this.$emit('update:showFilters', true)
        }
        resolve()
      })
    },
    getLocalStorageFilters: function () {
      return {
        users: this.filters.users.join(',')
      }
    },
    reloadTable: function () {
      const tableView = this.getTableReference()
      if (tableView) {
        tableView.refresh()
      }
    },
    getTableReference: function () {
      return this.$refs.virtualWaitingRoomRequestHistoryTable
    },
    getTeleconsultationDurationSeconds: function (row) {
      const teleconsultationEndAt = row.call_ended_at || row.audiologist_ended_at
      if (!row.started_at || !teleconsultationEndAt) {
        return null
      }
      return this.moment(teleconsultationEndAt).diff(this.moment(row.started_at), 'seconds')
    },
    getTotalDurationSeconds: function (row) {
      if (!row.started_at || !row.ended_at) {
        return null
      }
      return this.moment(row.ended_at).diff(this.moment(row.started_at), 'seconds')
    },
    getConclusionDurationSeconds: function (row) {
      const teleconsultationEndAt = row.call_ended_at || row.audiologist_ended_at
      if (!teleconsultationEndAt || !row.ended_at) {
        return null
      }
      const seconds = this.moment(row.ended_at).diff(this.moment(teleconsultationEndAt), 'seconds')
      return seconds >= 0 ? seconds : 0
    },
    onRowClicked: function ({ row }) {
      if (row.ended_at !== null) {
        this.$emit('row-clicked', { request: row })
      } else {
        this.showToast(this.$t('evocall.history.request_has_not_finished'), this.$store.state.errorToastOptions)
      }
    }
  },
  computed: {
    waitingRoomModuleKey: function () {
      return `virtualWaitingRoom`
    },
    ...mapState('auth', [
      'activities',
      'accessTypes'
    ]),
    ...mapState({
      infoTypes: function (state) {
        return state[this.waitingRoomModuleKey].infoTypes
      },
      accessTypes(state, getters) {
        return getters[`${this.waitingRoomModuleKey}/accessTypes`]
      },
      appointmentTypesOptions: function(state) {
        return state[this.waitingRoomModuleKey].appointmentTypes
      }
    }),
    virtualWaitingRoomService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.virtual_waiting_room_server) || null
    },
    activeFilters: function () {
      return Object.keys(this.filters).reduce((accumulator, currentValue) => accumulator + (Array.isArray(this.filters[currentValue]) ? (this.filters[currentValue].length > 0 ? 1 : 0) : typeof this.filters[currentValue] === 'object' ? Object.values(this.filters[currentValue]).some(value => value) ? 1 : 0 : this.filters[currentValue] !== '' ? 1 : 0), 0)
    },
    userPerformsActivities: function () {
      return this.users.find(user => user.id === this.$auth.user().id) !== undefined
    },
    hasFiltersToDisplay: function () {
      return !this.userPerformsActivities
    },
    readRequestsHistoryPermissions: function () {
      let permissions = []
      if (this.virtualWaitingRoomService !== null) {
        permissions = this.virtualWaitingRoomService.user_permissions.filter(p => p.info_type === this.infoTypes.HISTORY_REQUESTS && p.access_type >= this.accessTypes.READ)
      }
      return permissions
    },
    hasReadRequestsHistoryPermissions: function () {
      return this.readRequestsHistoryPermissions.length > 0
    },
    readableRequestHistoryEntities: function () {
      let entities = []
      if (this.readRequestsHistoryPermissions.some(p => p.entity_id === null)) {
        entities = [...this.virtualWaitingRoomService.entities]
      } else {
        entities = this.virtualWaitingRoomService.entities.filter(entity => this.readRequestsHistoryPermissions.some(p => p.entity_id === entity.id))
      }
      return entities
    },
    accepteesOptions: function () {
      return uniqBy(this.entityActivityUsersForPeriod
        .filter(entityActivityUser => entityActivityUser.activity.id === this.activities.AUDIOLOGY && entityActivityUser.hasOwnProperty('user') && entityActivityUser.user !== null)
        .map(entityActivityUser => entityActivityUser.user), 'id')
    },
    creatorsOptions: function () {
      const dispenserActivities = [this.activities.DISPENSER_FIELD, this.activities.DISPENSER_PREMISES]
      return uniqBy(this.entityActivityUsersForPeriod
        .filter(entityActivityUser => dispenserActivities.includes(entityActivityUser.activity.id) && entityActivityUser.hasOwnProperty('user') && entityActivityUser.user !== null)
        .map(entityActivityUser => entityActivityUser.user), 'id')
    },
    isSupervisor: function () {
      return this.virtualWaitingRoomService.user_permissions.some(permission => permission.info_type === this.infoTypes.REQUESTS && permission.access_type >= this.accessTypes.ADMIN)
    },
    displayColumns: function () {
      const columns = [...this.baseColumns]
      if (this.isSupervisor) {
        columns.push('conclusion_duration')
      }
      return columns.concat(this.tailColumns)
    },
    displayTableOptions: function () {
      const headings = { ...this.tableOptions.headings }
      if (this.isSupervisor) {
        headings.conclusion_duration = this.$t('evocall.history.conclusion_duration')
      }
      return {
        ...this.tableOptions,
        headings
      }
    },
  },
  watch: {
    customQueries: {
      handler: function (value) {
        if(value === null) return
        if (value.start && value.start !== '') {
          this.filters.dateRange.start = value.start
        } else {
          this.$nextTick(function () {
            this.$refs.virtualWaitingRoomRequestHistoryTable.customQueries.start = this.moment().subtract(1, 'months').format('YYYY-MM-DD')
          })
        }

        if (value.end && value.end !== '') {
          this.filters.dateRange.end = value.end
        } else {
          this.$nextTick(function () {
            this.$refs.virtualWaitingRoomRequestHistoryTable.customQueries.end = this.moment().format('YYYY-MM-DD')
          })
        }
      },
      immediate: true
    },
    querySearch: function (search) {
      if (search.length > 2 || search.length === 0) {
        this.getTableReference().setFilter(search)
      }
    },
    'filters.dateRange': function (dateRange) {
      if (this.hasLoadedLocalStorage) {
        this.$refs.virtualWaitingRoomRequestHistoryTable.customQueries.start = dateRange.start || ''
        this.emitTableFilter('vue-tables.virtualWaitingRoomRequestHistoryTable.filter::end', dateRange.end || '')
        this.loadEntityActivityUsersForPeriod(dateRange.start, dateRange.end)
      }
    },
    'filters.acceptees': function (acceptees) {
      if (this.hasLoadedLocalStorage && Array.isArray(acceptees)) {
        this.emitTableFilter('vue-tables.virtualWaitingRoomRequestHistoryTable.filter::acceptees', acceptees.map(acceptee => acceptee.id).join(','))
      }
    },
    'filters.creators': function (creators) {
      if (this.hasLoadedLocalStorage && Array.isArray(creators)) {
        this.emitTableFilter('vue-tables.virtualWaitingRoomRequestHistoryTable.filter::creators', creators.map(creator => creator.id).join(','))
      }
    },
    'filters.consultationTypes': function (consultationTypes) {
      if (this.hasLoadedLocalStorage && Array.isArray(consultationTypes)) {
        this.emitTableFilter('vue-tables.virtualWaitingRoomRequestHistoryTable.filter::consultationTypes', consultationTypes.map(cons => cons.id).join(','))
      }
    },
    'filters.appointmentTypes': function (appointmentTypes) {
      if (this.hasLoadedLocalStorage && Array.isArray(appointmentTypes)) {
        this.emitTableFilter('vue-tables.virtualWaitingRoomRequestHistoryTable.filter::appointmentTypes', appointmentTypes.flatMap(type => type.bdIds).join(','))
      }
    },
    activeFilters: function (nrFilters) {
      this.$emit('filters-updated', nrFilters)
    }
  }
}
</script>

<style lang="scss">
#virtualWaitingRoomRequestHistoryTable {
  $assetsPath: "../../../assets";
  @import '@/sass/icons/icons-classes.scss';

  i {
    display: block;
    width: 1.5rem;
    height: 1.5rem;
  }

  .i-has-comment-gray {
    @extend .i-has-comment-gray;
  }
}
</style>
